import React from "react"
import {
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector, GridToolbarExport,
    GridToolbarFilterButton, useGridApiContext
} from "@mui/x-data-grid-pro"
import {Button} from "@mui/material"
import Print from "@mui/icons-material/Print"
import {Menu, MenuItem} from "@mui/material"
import {ProductDTO} from "../TestTakerSearchTypes"
import {useHistory} from "react-router"
import {reportsStore} from "../../../stores/ReportStore"
import {ReportSubPaths} from "../../../../ReportSubPaths"
import {GroupTreeNode} from "../../../../../common/group/models/GroupMaster"
import {isPrintSupportedProduct} from "../TestTakerSearchUtils"

/** Due to performance concerns, we must print a limited number of individual reports at one time.
 *  If there are optimizations this limit may be removed. */
const INDIVIDUAL_REPORT_PRINT_LIMIT = 100

/** The GridToolbarContainer is considered legacy in modern MUI versions.
 *  But until we update MUI this is the option we have. */
export function TestTakerSearchToolbar() {

    const history = useHistory()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const menuIsOpen = Boolean(anchorEl)
    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const printIndividualReports = (studentGroupInfo: Map<number, GroupTreeNode[]>, productId: number) => {
        reportsStore.selectedTakeIds = Array.from(studentGroupInfo.keys()).map(key => Number(key))
        reportsStore.studentGroupInfo = studentGroupInfo
        history.push(ReportSubPaths.individualPrintSelected(productId))
    }

    const api = useGridApiContext()

    /** The individual reports page still requires a productId.
     *  This function gets a distinct list of products used to print only data associated with one product at a time. */
    function getProductsFromTable(): ProductDTO[] {
        const productIds: Set<number> = new Set()
        const products = Array.from(api.current.getSelectedRows().values())
            .map(row => row.product)
            .reduce((a: ProductDTO[], b: ProductDTO) => {
                if (!productIds.has(b.productId)) {
                    productIds.add(b.productId)
                    return [...a, b]
                }
                return a
            }, [])
        return products ?? []
    }

    function getStudentGroupInfo(limit: number, productId: number): Map<number, GroupTreeNode[]> {
        return new Map<number, GroupTreeNode[]>(
            Array.from(api.current.getSelectedRows()
                .values())
                .filter(row => row.product.productId === productId)
                .slice(0, limit)
                .map(row => [row.takeId, row.groupInfo])
        )
    }

    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton/>
            <GridToolbarFilterButton/>
            <GridToolbarDensitySelector
                slotProps={{tooltip: {title: 'Change density'}}}
            />
            <GridToolbarExport
                slotProps={{
                    tooltip: {title: 'Export data'},
                }}
            />
            <Button startIcon={<Print/>} size={"small"} onClick={(e) => handleOpen(e)}>Print</Button>
            <Menu
                id={"print-individual-reports-menu"}
                anchorEl={anchorEl}
                open={menuIsOpen}
                onClose={handleClose}
            >
                {getProductsFromTable().map(product => {
                    return (
                        <MenuItem
                            disabled={!isPrintSupportedProduct(product.productId)}
                            key={product.productId}
                                  onClick={() => printIndividualReports(getStudentGroupInfo(INDIVIDUAL_REPORT_PRINT_LIMIT, product.productId), product.productId)}
                        >
                            Print Individual Reports
                            for {product.productDisplayName} (max: {INDIVIDUAL_REPORT_PRINT_LIMIT})
                            {!isPrintSupportedProduct(product.productId) && '— not yet printable'}
                        </MenuItem>
                    )
                })}
            </Menu>
        </GridToolbarContainer>
    );
}